/*
  PCG live slab gallery (storefront slab gallery, spec §6.4 R17–R21, R18a, D22, D31, and
  Robert's follow-up calls D32 no layout jump, D33 progressive reveal, and the 2026-10-26 round:
  one rotating grid of 12, the "random selection" line, the real total line, thumbnails).

  Turns a main-gallery_01 / main-gallery_02 section into a live grid of recent gradings, drawn
  as slabs by pcg-slab.js, and falls back to the section's own image blocks when the feed is
  down or too thin.

  Markup (from the section's Liquid):
    .pcg-live-gallery[data-live-kind][data-frame-url][data-api-base]   the section root
      .headingblock h2                 the section heading
      .pcg-live-gallery__meta          the lines under the heading, shown only while live (R18a):
        p.pcg-live-gallery__selection    live_selection_note, then the graded date range
        p.pcg-live-gallery__total        live_total_note filled from /counts (data-template)
        p.pcg-live-gallery__note         the D31 note
      .pcg-live-gallery__live          the Staff picks row and the grid row go here
      .galleray-card                   the image blocks: the fallback (R20)
  The section's inline script sets data-live-state="pending" before this file runs and draws
  placeholders into .pcg-live-gallery__live: a Staff picks row of as many slabs as the last
  visit had (remembered under localStorage "pcg-live-gallery:<root id>" as { p }), then one
  grid of 12. The meta lines take their space unseen. So from the first paint the section
  stands at its final height, and is never blank (D32, D33). It also falls back by itself if
  this file never starts.

  The feed's days[].slabs are flattened, newest first, then its spares. The grid is always 12
  slots: the first 12 slabs, the rest held back as spares. Day headings are gone.

  States (data-live-state): pending -> live -> (data-live-settled) or pending|live -> fallback.
  - live: set as soon as the feed arrives. Real slots replace the placeholders, each with its
    caption ("Pokémon · 25 Oct") and a navy placeholder; each slab fades in once its card image
    (and the frame) is ready (D33).
  - Images: a slab's thumbUrl when it has one, else its imageUrl. A thumb that fails is redrawn
    from imageUrl in the same slot; only when that fails too is the slab failed. A failed slab
    is replaced in place by the next spare that has not failed. With no spare left it keeps its
    placeholder until the section settles live; then it becomes a gap: hidden, still taking
    its space, where it stands while any of the section is on screen, and moved to the end of
    the grid once the section is wholly off screen (where, on phones, it takes none). Staff
    picks get no spare; a failed pick goes. Spares are drawn up front in a hidden holder, from
    their thumbs, so a replacement is usually ready at once.
  - One 8-second budget runs from the start of the fetch (R20). The section falls back when the
    request fails or is unanswered at the deadline, when the feed has fewer than 4 day slabs,
    or when every slab on screen has settled (or the deadline has come) and fewer than 4 grid
    slabs can be shown; Staff picks do not count. At the deadline a slab still loading counts
    as failed: a spare that has already loaded takes its place, else it becomes a gap. Once 4
    grid slabs have loaded the section stays live (loaded slabs are never removed).

  Rotation: once settled live, the feed is fetched again every 3 minutes while the page is
  visible (on becoming visible, at once if 3 minutes have passed). Slots are stable: a slab
  still in the new first 12 keeps its slot; a new slab is loaded unseen and, once its image is
  ready, takes the slot of a slab that dropped out (else a gap), fading in over it. A new slab
  that fails gives way to the next one; with none left the old slab stays, unless it is now a
  Staff pick: a slab in the picks row (or the feed's picks) never stays in the grid, and with
  nothing to take its slot it leaves a gap there. Staff picks change in place while the row
  keeps its height; otherwise only while the section is wholly below the screen, or wholly
  above it when the row only shrinks or goes. A failed fetch keeps what is on screen.
  Rotation never changes the height of the section on screen.

  The date range under the heading comes from the real completedOn of the grid slabs on screen
  (picks excluded), in the visitor's zone, and is redrawn whenever they change. The total line
  comes from one shared GET /public/api/v1/counts per page: monthGraded, the UTC month (the
  backend counts from the first of the UTC month); left blank when it fails or is 0. When the
  response names its month (monthStart, "YYYY-MM-DD") the line shows only if that is the
  current UTC month; without it, the line is left blank for the first 15 minutes of the 1st
  (UTC), while the backend's cache may still hold last month's total. Its line is reserved from the first paint either way, so it never moves anything.

  No jump (D32): the grid is 12 slots from the first paint to the end, and every change of
  rows or state goes through holdHeight(). While any part of the section is on or above the
  screen its height may grow but never shrink: the root's min-height holds the tallest height
  drawn, so a lost picks row or the fallback leaves space at the bottom rather than pulling up
  what is below. While the section is wholly below the screen it takes its natural height. The
  hold is released when the window's width changes. The only shift left is a picks row the
  last visit did not have.

  Every text goes in with textContent, and every image URL must be http(s). Slabs link to their
  pop-report cert page, which is pcg-slab.js's default href (D10).

  Zoom (Robert, 2026-10-26): a magnifier at the bottom right of each shown slab's item, beside
  its caption, opens one shared large view of that slab, drawn again from its full image (its
  thumb if that fails). The large view has a solid black round close button; Esc, the button or
  a click on the backdrop outside the panel closes it, and focus goes back to the magnifier.
*/
(function () {
  if (window.PCGLiveGallery) return;

  const VERSION = '1.3.0';
  const DEFAULT_API_BASE = 'https://app.premiercardgrading.com';
  const API_PATH = '/public/api/v1/gallery';
  const COUNTS_PATH = '/public/api/v1/counts';
  const BUDGET_MS = 8000;
  const ROTATE_MS = 3 * 60 * 1000;
  const FADE_MS = 450;
  const DECODE_MS = 2000;
  const SLOTS = 12;
  const MIN_SLABS = 4;
  const STORE_KEY = 'pcg-live-gallery:';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December'];
  const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
  /* The renderer's own sizes (pcg-slab.css): the frame, and the card window within it. */
  const FRAME_SIZE = [946, 1561];
  const CARD_SIZE = [700, 982];

  function text(v) {
    return v == null ? '' : String(v).trim();
  }

  function make(tag, className, parent) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (parent) parent.appendChild(node);
    return node;
  }

  function list(v) {
    return Array.isArray(v) ? v : [];
  }

  function httpUrl(v) {
    const s = text(v);
    if (!/^https?:\/\//i.test(s)) return '';
    try {
      new URL(s);
    } catch (e) {
      return '';
    }
    return s;
  }

  let ZONE = 'UTC';
  try {
    ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (e) {
    /* keep UTC (R3) */
  }

  /* ── Dates, in the visitor's zone ─────────────────────────────────────────────────── */

  const formats = {};
  function isoDateIn(tz, when) {
    const fmt = (zone) => {
      if (!formats[zone]) formats[zone] = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' });
      return formats[zone];
    };
    let parts;
    try {
      parts = fmt(tz || 'UTC').formatToParts(when);
    } catch (e) {
      parts = fmt('UTC').formatToParts(when);
    }
    const get = (type) => (parts.find((p) => p.type === type) || {}).value;
    return get('year') + '-' + get('month') + '-' + get('day');
  }

  /* { y, m, d, key: 'YYYY-MM-DD', n: days since 1970 } for an instant, or null. */
  function zoneDay(value, tz) {
    if (!(value instanceof Date) && !text(value)) return null;
    const when = value instanceof Date ? value : new Date(text(value));
    if (isNaN(when.getTime())) return null;
    const key = isoDateIn(tz, when);
    const m = DATE_RE.exec(key);
    if (!m) return null;
    return { y: +m[1], m: +m[2], d: +m[3], key, n: Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000 };
  }

  function dayLabel(day, withYear) {
    return day.d + ' ' + MONTHS[day.m - 1] + (withYear ? ' ' + day.y : '');
  }

  /* "Graded 25 Oct", "Graded 20–26 Oct", "Graded 28 Sep – 3 Oct", "Graded 28 Dec 2025 – 3 Jan",
     "Graded since 3 Aug" (over 30 days); a year only where it is not the current one. */
  function gradedRange(values, tz, now) {
    const days = list(values).map((v) => zoneDay(v, tz)).filter(Boolean).sort((a, b) => a.n - b.n);
    if (!days.length) return '';
    const first = days[0];
    const last = days[days.length - 1];
    const year = (zoneDay(now || new Date(), tz) || last).y;
    if (last.n - first.n > 30) return 'Graded since ' + dayLabel(first, first.y !== year);
    if (first.n === last.n) return 'Graded ' + dayLabel(first, first.y !== year);
    if (first.y === last.y && first.m === last.m) return 'Graded ' + first.d + '–' + dayLabel(last, last.y !== year);
    return 'Graded ' + dayLabel(first, first.y !== last.y) + ' – ' + dayLabel(last, last.y !== year);
  }

  /* [game, day] for the caption "Pokémon · 25 Oct" (G5). */
  function captionParts(slab, tz, now) {
    const day = zoneDay(slab && slab.completedOn, tz);
    const year = (zoneDay(now || new Date(), tz) || {}).y;
    return [text(slab && slab.gameName), day ? dayLabel(day, day.y !== year) : ''];
  }

  function caption(slab, tz, now) {
    return captionParts(slab, tz, now).filter(Boolean).join(' · ');
  }

  /* "{count} cards graded in {month} across our offices"; '' when there is no real count. */
  function totalLine(template, counts, now) {
    const n = counts && typeof counts.monthGraded === 'number' ? counts.monthGraded : NaN;
    if (!text(template) || !isFinite(n) || n < 1) return '';
    const at = now || new Date();
    let month = at.getUTCMonth();
    if (counts.monthStart != null) {
      /* The UTC month the count is from: nothing unless it is the current one. */
      const m = DATE_RE.exec(text(counts.monthStart));
      if (!m || +m[1] !== at.getUTCFullYear() || +m[2] !== month + 1) return '';
    } else if (at.getUTCDate() === 1 && at.getUTCHours() === 0 && at.getUTCMinutes() < 15) {
      /* Without monthStart, the backend's counts cache can still hold last month's total just
         after 00:00 UTC on the 1st: say nothing for the first quarter hour (the visitor's
         clock; a guard). */
      return '';
    }
    const count = new Intl.NumberFormat('en-US').format(Math.floor(n));
    month = MONTH_NAMES[month];
    return text(template).replace(/\{count\}/g, () => count).replace(/\{month\}/g, () => month);
  }

  /* One counts request per API server per page, shared by both sections. */
  const countsFor = {};
  function counts(apiBase) {
    if (!countsFor[apiBase]) {
      countsFor[apiBase] = fetch(apiBase + COUNTS_PATH, { credentials: 'omit' })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null);
    }
    return countsFor[apiBase];
  }

  function rotateEvery() {
    const test = window.PCGLiveGallery && window.PCGLiveGallery._test;
    return (test && test.rotateMs) || ROTATE_MS;
  }

  /* One resize and one visibilitychange listener for every section; a section whose root has
     left the page (the theme editor re-renders sections) is dropped. */
  const sections = new Set();
  function each(event) {
    sections.forEach((section) => {
      if (section.root.isConnected) {
        section[event]();
        return;
      }
      section.stop();
      sections.delete(section);
    });
  }
  window.addEventListener('resize', () => each('resize'));
  document.addEventListener('visibilitychange', () => each('visible'));

  function flatten(feed) {
    const seen = {};
    const fresh = (slab) => {
      const id = slab && typeof slab === 'object' ? text(slab.gradedId) : '';
      if (!id || seen[id]) return false;
      seen[id] = true;
      return true;
    };
    const picks = list(feed.picks).filter(fresh);
    const days = list(feed.days).reduce((all, d) => all.concat(d ? list(d.slabs) : []), []).filter(fresh);
    return { picks, days, spares: list(feed.spares).filter(fresh) };
  }

  /* ── One section ──────────────────────────────────────────────────────────────────── */

  function init(root) {
    if (!root || root.hasAttribute('data-live-started')) return;
    const state = root.getAttribute('data-live-state');
    if (state && state !== 'pending') return;
    root.setAttribute('data-live-started', '');
    root.setAttribute('data-live-state', 'pending');

    const kind = text(root.getAttribute('data-live-kind'));
    const frameUrl = text(root.getAttribute('data-frame-url'));
    const apiBase = (text(root.getAttribute('data-api-base')) || DEFAULT_API_BASE).replace(/\/+$/, '');
    const live = root.querySelector('.pcg-live-gallery__live');
    const meta = root.querySelector('.pcg-live-gallery__meta');
    const range = root.querySelector('.pcg-live-gallery__range');
    const total = root.querySelector('.pcg-live-gallery__total');

    let done = false;
    let built = false;
    let deadline = null;
    let observer = null;
    let controller = null;
    let holder = null;
    let spares = [];
    let grid = null;
    let held = 0;
    /* Every drawn slab's data, and the full image to retry a failed thumb with. */
    const dataOf = new WeakMap();
    const retryWith = new WeakMap();
    /* gradedIds whose image failed both ways: rotation skips them. */
    const failedIds = {};
    /* Rotation: the last fetch, the next one, and the new slabs loading unseen in the stage,
       each staged item -> { target item, queue of slabs to try after it }. */
    let lastFetch = Date.now();
    let timer = null;
    let gapWatch = null;
    /* The gradedIds pinned at the last rotation: kept out of the grid. */
    let pinned = {};
    let fetching = false;
    let stage = null;
    const entries = new Map();

    /* Zoom: the magnifier opens the large view of the slab now in its item. */
    root.addEventListener('click', (e) => {
      const btn = e.target && e.target.closest ? e.target.closest('.pcg-live-gallery__zoom') : null;
      if (!btn || !root.contains(btn)) return;
      const item = btn.closest('.pcg-live-gallery__item');
      const a = item && anchorOf(item);
      const slab = a && dataOf.get(a);
      if (slab && window.PCGSlab) openZoom(slab, frameUrl, btn);
    });

    /* D32: see the header. */
    function holdHeight(change) {
      const before = root.getBoundingClientRect().height;
      change();
      if (root.getBoundingClientRect().top >= window.innerHeight) {
        held = 0;
        root.style.minHeight = '';
        return;
      }
      held = Math.max(held, before);
      root.style.minHeight = held + 'px';
    }

    let width = window.innerWidth;
    sections.add({
      root,
      resize() {
        if (window.innerWidth === width) return;
        width = window.innerWidth;
        held = 0;
        root.style.minHeight = '';
      },
      visible() {
        if (!root.hasAttribute('data-live-settled') || document.hidden) return;
        if (Date.now() - lastFetch >= rotateEvery()) refresh();
      },
      stop() {
        clearTimeout(timer);
        clearTimeout(deadline);
        if (observer) observer.disconnect();
        if (gapWatch) gapWatch.disconnect();
      }
    });

    function finish(outcome) {
      if (done) return;
      done = true;
      clearTimeout(deadline);
      if (controller) controller.abort();
      holdHeight(() => {
        if (holder) holder.remove();
        holder = null;
        if (outcome === 'live') {
          root.setAttribute('data-live-settled', '');
          return;
        }
        if (observer) observer.disconnect();
        live.hidden = true;
        live.textContent = '';
        if (meta) meta.hidden = true;
        root.setAttribute('data-live-state', 'fallback');
      });
      if (outcome !== 'live') return;
      schedule();
      /* Gaps left in place go to the end once the section is off screen. */
      if (window.IntersectionObserver && grid && grid.querySelector('[data-gap=""]')) {
        gapWatch = new IntersectionObserver((seen) => {
          if (!seen[seen.length - 1].isIntersecting) compactGaps();
        });
        gapWatch.observe(root);
      }
    }

    if ((kind !== 'tcg' && kind !== 'sport') || !frameUrl || !live || !window.PCGSlab || !window.fetch) {
      finish('fallback');
      return;
    }

    if (total) {
      counts(apiBase).then((data) => {
        total.textContent = totalLine(total.getAttribute('data-template'), data, new Date());
      });
    }

    /* The slab drawn last in an item's slot (two while one fades in over the other). */
    function anchorOf(item) {
      const all = item.querySelectorAll('.pcg-live-gallery__slot > .pcg-slab');
      return all.length ? all[all.length - 1] : null;
    }

    function status(item) {
      const a = anchorOf(item);
      return a ? a.getAttribute('data-image') : null;
    }

    function idOf(item) {
      const a = anchorOf(item);
      const slab = a && dataOf.get(a);
      return slab ? text(slab.gradedId) : '';
    }

    function rowItems(name) {
      return Array.prototype.slice.call(live.querySelectorAll('.pcg-live-gallery__row[data-row="' + name + '"] .pcg-live-gallery__item'));
    }

    /* Staff picks and the grid; not the hidden holders. */
    function shownItems() {
      return Array.prototype.slice.call(live.querySelectorAll('.pcg-live-gallery__row .pcg-live-gallery__item'));
    }

    function onScreen(item) {
      return !item.hasAttribute('data-failed') && !item.hasAttribute('data-gap') && !!anchorOf(item);
    }

    function gridCount() {
      return rowItems('grid').filter(onScreen).length;
    }

    function updateRange() {
      if (!range) return;
      const dates = rowItems('grid').filter(onScreen).map((it) => (dataOf.get(anchorOf(it)) || {}).completedOn);
      range.textContent = gradedRange(dates, ZONE, new Date());
    }

    function addRow(name, heading) {
      const row = make('div', 'pcg-live-gallery__row', live);
      row.setAttribute('data-row', name);
      if (heading) make('h3', 'pcg-live-gallery__heading', row).textContent = heading;
      return make('div', 'pcg-live-gallery__grid', row);
    }

    /* A hidden holder for slabs loading unseen, first in .pcg-live-gallery__live so the last row
       stays :last-child. */
    function addHolder() {
      const box = make('div', 'pcg-live-gallery__spares');
      box.hidden = true;
      box.setAttribute('aria-hidden', 'true');
      live.insertBefore(box, live.firstChild);
      return box;
    }

    function addItem(parent) {
      const item = make('div', 'pcg-live-gallery__item', parent);
      make('div', 'pcg-live-gallery__slot', item);
      make('p', 'pcg-live-gallery__caption', item);
      /* Zoom: CSS shows it once the item's slab is ready. */
      const btn = make('button', 'pcg-live-gallery__zoom', item);
      btn.type = 'button';
      btn.setAttribute('aria-label', 'Enlarge this slab');
      btn.appendChild(icon(['c', 'm20 20-3.5-3.5', 'M11 8v6', 'M8 11h6'], 14, 2.4));
      return item;
    }

    /* D33: data-ready (which fades the slab in over its placeholder) once the card image has
       loaded and the frame has settled, both decoded so the fade does not start on a blank. */
    /* Called once both images have loaded. decode() can stay pending for an image that is not
       rendered (WebKit, in the hidden stage), so it races a timer, and a decode() that rejects
       after the load counts as done. */
    function whenDecoded(a, then) {
      const frame = a.querySelector('.pcg-slab__frame');
      const card = a.querySelector('.pcg-slab__card');
      const decoded = Promise.all([frame, card].map((img) => (img && img.decode ? img.decode().catch(() => {}) : null)));
      let called = false;
      const once = () => {
        if (called) return;
        called = true;
        then();
      };
      decoded.then(once);
      setTimeout(once, DECODE_MS);
    }

    function ready(a) {
      const frame = a.querySelector('.pcg-slab__frame');
      return a.getAttribute('data-image') === 'loaded' && !(frame && !frame.complete);
    }

    function reveal(item) {
      if (item.hasAttribute('data-ready') || !item.closest('.pcg-live-gallery__row')) return;
      const a = anchorOf(item);
      if (!a || !ready(a)) return;
      whenDecoded(a, () => {
        requestAnimationFrame(() => {
          if (anchorOf(item) === a) item.setAttribute('data-ready', '');
        });
      });
    }

    /* G7: the thumb first, imageUrl when there is none (or as the retry). No loading="lazy":
       R20's 8-second budget counts a slab that has not loaded as failed, and a lazy image below
       the fold would not start. The renderer already sets decoding="async". */
    function draw(slab, imageUrl) {
      const thumb = httpUrl(slab.thumbUrl);
      const full = httpUrl(slab.imageUrl);
      const url = imageUrl || thumb || full;
      const a = window.PCGSlab.render(Object.assign({}, slab, { imageUrl: url }), { frameUrl });
      dataOf.set(a, slab);
      if (!imageUrl && thumb && full && thumb !== full) retryWith.set(a, full);
      const frame = a.querySelector('.pcg-slab__frame');
      const card = a.querySelector('.pcg-slab__card');
      [[frame, FRAME_SIZE], [card, CARD_SIZE]].forEach(([img, size]) => {
        if (!img) return;
        img.setAttribute('width', size[0]);
        img.setAttribute('height', size[1]);
      });
      if (frame && !frame.complete) {
        const onFrame = () => {
          const item = a.closest('.pcg-live-gallery__item');
          if (item) onImage(item);
        };
        frame.addEventListener('load', onFrame);
        frame.addEventListener('error', onFrame);
      }
      return a;
    }

    /* The game name gives way (ellipsis) before the date does; CSS draws the " · ". */
    function setCaption(item, slab) {
      const p = item.querySelector('.pcg-live-gallery__caption');
      const parts = captionParts(slab, ZONE, new Date());
      p.textContent = '';
      make('span', 'pcg-live-gallery__game', p).textContent = parts[0];
      make('span', 'pcg-live-gallery__date', p).textContent = parts[1];
      const btn = item.querySelector('.pcg-live-gallery__zoom');
      const name = text(slab && slab.nameLine);
      if (btn) btn.setAttribute('aria-label', name ? 'Enlarge ' + name : 'Enlarge this slab');
    }

    /* The slab goes into the page as soon as it is drawn, so its image events reach root. */
    function fill(item, slab) {
      item.querySelector('.pcg-live-gallery__slot').appendChild(draw(slab));
      setCaption(item, slab);
      return item;
    }

    /* D33: the next spare that has not failed takes the slab's slot; at the deadline only a
       spare already loaded will do. With none, the slab keeps its placeholder, marked failed,
       until the section settles (toGaps). */
    function replace(item, onlyLoaded) {
      if (item.closest('.pcg-live-gallery__row[data-row="grid"]')) {
        while (spares.length) {
          const spare = spares.shift();
          const s = status(spare);
          if (s === 'error' || (onlyLoaded && s !== 'loaded')) {
            spare.remove();
            continue;
          }
          item.parentNode.replaceChild(spare, item);
          reveal(spare);
          updateRange();
          return;
        }
      }
      item.setAttribute('data-failed', '');
      updateRange();
    }

    function offScreen() {
      const r = root.getBoundingClientRect();
      return r.bottom <= 0 || r.top >= window.innerHeight;
    }

    /* G2: a gap (data-gap) is hidden but keeps its space. While any of the section is on screen
       it stays where it is, since moving the slabs after it would be a visible shift; while the
       section is wholly off screen it goes to the end of the grid (data-gap="end"), where on
       phones it takes no space at all. */
    function compactGaps() {
      if (!grid || !offScreen()) return;
      grid.querySelectorAll('.pcg-live-gallery__item[data-gap]').forEach((item) => {
        item.setAttribute('data-gap', 'end');
        grid.appendChild(item);
      });
    }

    function makeGap(item) {
      item.removeAttribute('data-failed');
      item.removeAttribute('data-ready');
      item.setAttribute('data-gap', '');
      item.querySelector('.pcg-live-gallery__slot').textContent = '';
      item.querySelector('.pcg-live-gallery__caption').textContent = '';
    }

    /* A failed pick goes, and a picks row left empty goes with it. */
    function dropPick(item) {
      const row = item.closest('.pcg-live-gallery__row');
      item.remove();
      if (!row.querySelector('.pcg-live-gallery__item')) row.remove();
    }

    /* At settle: a failed grid slab becomes a gap, a failed pick goes. */
    function toGaps() {
      live.querySelectorAll('.pcg-live-gallery__row .pcg-live-gallery__item[data-failed]').forEach((item) => {
        if (item.closest('.pcg-live-gallery__row[data-row="grid"]')) makeGap(item);
        else dropPick(item);
      });
      compactGaps();
      updateRange();
    }

    function settle(atDeadline) {
      if (done) return;
      if (atDeadline) {
        shownItems()
          .filter((it) => onScreen(it) && status(it) !== 'loaded')
          .forEach((it) => replace(it, true));
      }
      if (gridCount() < MIN_SLABS) {
        finish('fallback');
        return;
      }
      holdHeight(toGaps);
      finish('live');
    }

    function checkSettled() {
      if (done || !built) return;
      if (!shownItems().some((it) => status(it) === 'loading')) settle(false);
    }

    /* G7: a thumb that fails is redrawn from imageUrl where it stands. */
    function retryFull(a) {
      const full = retryWith.get(a);
      const slab = dataOf.get(a);
      if (!full || !slab || !a.parentNode) return false;
      retryWith.delete(a);
      a.parentNode.replaceChild(draw(slab, full), a);
      return true;
    }

    root.addEventListener('pcgslab:imageerror', (e) => {
      const a = e.target;
      if (!built || !a || !a.closest || retryFull(a)) return;
      const slab = dataOf.get(a);
      if (slab) failedIds[text(slab.gradedId)] = true;
      const item = a.closest('.pcg-live-gallery__item');
      if (!item) return;
      if (stage && stage.contains(item)) {
        failedEntry(item);
        return;
      }
      /* Once settled only a pick can still fail (in a row redrawn by rotation): it goes. */
      if (done) {
        if (item.closest('.pcg-live-gallery__row[data-row="picks"]')) {
          holdHeight(() => dropPick(item));
          remember({ p: rowItems('picks').length });
        }
        return;
      }
      /* A failed spare is skipped when its turn comes. */
      if (holder && holder.contains(item)) return;
      replace(item, false);
      checkSettled();
    });

    function onImage(item) {
      if (stage && stage.contains(item)) landEntry(item);
      else reveal(item);
    }

    function remember(shape) {
      if (!root.id) return;
      try {
        window.localStorage.setItem(STORE_KEY + root.id, JSON.stringify(shape));
      } catch (e) {
        /* private mode or storage blocked: the next visit uses the default placeholders */
      }
    }

    function addPicks(picks) {
      const row = addRow('picks', 'Staff picks').parentNode;
      live.insertBefore(row, grid.parentNode);
      picks.forEach((slab) => fill(addItem(row.lastChild), slab));
    }

    function build(feed) {
      const f = flatten(feed);
      /* Spares only stand in for failures, so a feed short of 4 day slabs can never reach 4. */
      if (f.days.length < MIN_SLABS) {
        finish('fallback');
        return;
      }
      const pool = f.days.concat(f.spares);

      holdHeight(() => {
        live.textContent = '';
        holder = addHolder();
        grid = addRow('grid', null);
        if (f.picks.length) addPicks(f.picks);
        for (let i = 0; i < SLOTS; i++) {
          const item = addItem(grid);
          if (pool[i]) fill(item, pool[i]);
          else item.setAttribute('data-gap', 'end');
        }
        spares = pool.slice(SLOTS).map((slab) => fill(addItem(holder), slab));
        live.hidden = false;
        if (meta) meta.hidden = false;
        updateRange();
        root.setAttribute('data-live-state', 'live');
      });
      built = true;
      remember({ p: f.picks.length });

      observer = new MutationObserver((records) => {
        records.forEach((r) => {
          const item = r.target.closest && r.target.closest('.pcg-live-gallery__item');
          if (item) onImage(item);
        });
        checkSettled();
      });
      observer.observe(live, { subtree: true, attributes: true, attributeFilter: ['data-image'] });
      checkSettled();
    }

    const url = apiBase + API_PATH + '?kind=' + encodeURIComponent(kind) + '&tz=' + encodeURIComponent(ZONE);

    /* ── Rotation (G6) ──────────────────────────────────────────────────────────────── */

    function schedule() {
      clearTimeout(timer);
      const every = rotateEvery();
      timer = setTimeout(() => {
        if (root.isConnected && !document.hidden) refresh();
      }, Math.max(0, lastFetch + every - Date.now()));
    }

    function refresh() {
      if (fetching) return;
      fetching = true;
      lastFetch = Date.now();
      const ctl = window.AbortController ? new AbortController() : null;
      const stop = setTimeout(() => ctl && ctl.abort(), BUDGET_MS);
      fetch(url, { credentials: 'omit', signal: ctl ? ctl.signal : undefined })
        .then((res) => {
          if (!res.ok) throw new Error('gallery feed ' + res.status);
          return res.json();
        })
        .then((feed) => {
          if (feed && typeof feed === 'object' && root.isConnected) rotate(feed);
        })
        .catch(() => {
          /* keep what is on screen */
        })
        .then(() => {
          clearTimeout(stop);
          fetching = false;
          if (root.isConnected) schedule();
        });
    }

    /* Whether a slab was staged for the target. */
    function stageEntry(target, queue) {
      while (queue.length) {
        const slab = queue.shift();
        if (failedIds[text(slab.gradedId)]) continue;
        const item = fill(addItem(stage), slab);
        entries.set(item, { target, queue });
        if (ready(anchorOf(item))) landEntry(item);
        return true;
      }
      return false;
    }

    function failedEntry(item) {
      const entry = entries.get(item);
      entries.delete(item);
      item.remove();
      if (!entry || stageEntry(entry.target, entry.queue)) return;
      if (entry.target.closest('.pcg-live-gallery__row[data-row="grid"]') && pinned[idOf(entry.target)]) {
        makeGap(entry.target);
        updateRange();
      }
    }

    /* The new slab takes its target slot: over the old slab with a fade, or into a gap. */
    function landEntry(item) {
      const entry = entries.get(item);
      const a = anchorOf(item);
      if (!entry || !a || !ready(a) || item.hasAttribute('data-landing')) return;
      item.setAttribute('data-landing', '');
      whenDecoded(a, () => {
        if (entries.get(item) !== entry || !stage || !stage.contains(item)) return;
        entries.delete(item);
        const target = entry.target;
        if (!target.isConnected) {
          item.remove();
          return;
        }
        const slot = target.querySelector('.pcg-live-gallery__slot');
        const old = anchorOf(target);
        const gap = target.hasAttribute('data-gap') || !old;
        if (gap) {
          target.removeAttribute('data-gap');
          target.removeAttribute('data-ready');
          slot.textContent = '';
        } else {
          a.setAttribute('data-live-in', '');
        }
        slot.appendChild(a);
        setCaption(target, dataOf.get(a));
        item.remove();
        updateRange();
        if (gap) {
          reveal(target);
          return;
        }
        requestAnimationFrame(() => requestAnimationFrame(() => a.removeAttribute('data-live-in')));
        const still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        setTimeout(() => {
          if (old.parentNode && anchorOf(target) !== old) old.remove();
        }, still ? 0 : FADE_MS + 100);
      });
    }

    function rotate(feed) {
      const f = flatten(feed);
      if (f.days.length < MIN_SLABS) return;
      if (stage) stage.remove();
      entries.clear();
      stage = addHolder();

      compactGaps();
      const items = rowItems('grid');
      /* A slab is never in the picks row and the grid at once: the picks on screen and the
         feed's picks stay out of the grid. */
      pinned = {};
      rowItems('picks').map(idOf).concat(f.picks.map((s) => text(s.gradedId))).forEach((id) => {
        if (id) pinned[id] = true;
      });
      const shown = {};
      items.forEach((it) => {
        if (idOf(it) && !it.hasAttribute('data-gap')) shown[idOf(it)] = true;
      });
      const pool = f.days.concat(f.spares).filter((s) => !failedIds[text(s.gradedId)] && !pinned[text(s.gradedId)]);
      const want = {};
      pool.slice(0, SLOTS).forEach((s) => {
        want[text(s.gradedId)] = true;
      });
      /* New slabs in feed order, then the rest of the feed for gaps and failures. */
      const queue = pool.slice(0, SLOTS).filter((s) => !shown[text(s.gradedId)])
        .concat(pool.slice(SLOTS).filter((s) => !shown[text(s.gradedId)]));
      const leaving = items.filter((it) => !it.hasAttribute('data-gap') && idOf(it) && !want[idOf(it)]);
      const gaps = items.filter((it) => it.hasAttribute('data-gap') || !idOf(it));
      leaving.concat(gaps).forEach((target) => {
        /* A grid slab now pinned with nothing to replace it leaves its slot as a gap. */
        if (!stageEntry(target, queue) && pinned[idOf(target)]) makeGap(target);
      });
      updateRange();

      rotatePicks(f.picks);
    }

    /* Whether a picks row of b slabs stands as tall as one of a (phones: one swipe row). */
    function sameRows(gridEl, a, b) {
      const style = window.getComputedStyle(gridEl);
      if (style.display === 'flex') return true;
      const cols = style.gridTemplateColumns.split(' ').filter(Boolean).length || 1;
      return Math.ceil(a / cols) === Math.ceil(b / cols);
    }

    function rotatePicks(picks) {
      const now = rowItems('picks');
      const ids = picks.map((s) => text(s.gradedId));
      if (now.map(idOf).join() === ids.join()) return;
      const row = live.querySelector('.pcg-live-gallery__row[data-row="picks"]');
      /* In place while the row keeps its height: a changed pick fades in over the old one, a
         pick no longer in the feed goes, a new one is added at the end. */
      if (row && picks.length && sameRows(row.lastChild, now.length, picks.length)) {
        now.forEach((it, i) => {
          if (i >= picks.length) it.remove();
          else if (idOf(it) !== ids[i]) stageEntry(it, [picks[i]]);
        });
        for (let i = now.length; i < picks.length; i++) fill(addItem(row.lastChild), picks[i]);
        remember({ p: picks.length });
        return;
      }
      /* A picks row of another height: only while nothing visible moves, i.e. the section is
         wholly below the screen, or wholly above it and the row only shrinks or goes (the
         height hold keeps its space, so nothing on screen moves). */
      const r = root.getBoundingClientRect();
      if (r.top < window.innerHeight && !(r.bottom <= 0 && picks.length < now.length)) return;
      holdHeight(() => {
        const row = live.querySelector('.pcg-live-gallery__row[data-row="picks"]');
        if (row) row.remove();
        if (picks.length) addPicks(picks);
      });
      remember({ p: picks.length });
    }

    deadline = setTimeout(() => {
      if (built) settle(true);
      else finish('fallback');
    }, BUDGET_MS);
    controller = window.AbortController ? new AbortController() : null;
    fetch(url, { credentials: 'omit', signal: controller ? controller.signal : undefined })
      .then((res) => {
        if (!res.ok) throw new Error('gallery feed ' + res.status);
        return res.json();
      })
      .then((feed) => {
        if (done) return;
        if (!feed || typeof feed !== 'object') throw new Error('gallery feed shape');
        build(feed);
      })
      .catch(() => finish('fallback'));
  }

  /* -- Zoom: the shared large view ---------------------------------------------------- */

  const SVG_NS = 'http://www.w3.org/2000/svg';
  let zoom = null;
  let zoomFrom = null;

  /* A stroked 24x24 icon; 'c' is the magnifier's lens. */
  function icon(paths, size, width) {
    const svg = document.createElementNS(SVG_NS, 'svg');
    [['viewBox', '0 0 24 24'], ['width', size], ['height', size], ['fill', 'none'], ['stroke', 'currentColor'],
      ['stroke-width', width], ['stroke-linecap', 'round'], ['stroke-linejoin', 'round'], ['aria-hidden', 'true'],
      ['focusable', 'false']].forEach(([k, v]) => svg.setAttribute(k, v));
    paths.forEach((d) => {
      const lens = d === 'c';
      const node = document.createElementNS(SVG_NS, lens ? 'circle' : 'path');
      if (lens) [['cx', '11'], ['cy', '11'], ['r', '7']].forEach(([k, v]) => node.setAttribute(k, v));
      else node.setAttribute('d', d);
      svg.appendChild(node);
    });
    return svg;
  }

  function closeZoom() {
    if (!zoom || zoom.box.hidden) return;
    zoom.box.hidden = true;
    zoom.slab.textContent = '';
    document.documentElement.classList.remove('pcg-live-zoom-open');
    /* Back to the magnifier; if its slab went while the view was open, to its item's slab. */
    if (zoomFrom && document.contains(zoomFrom)) {
      zoomFrom.focus();
      if (document.activeElement !== zoomFrom) {
        const slab = zoomFrom.parentNode && zoomFrom.parentNode.querySelector('.pcg-slab');
        if (slab) slab.focus();
      }
    }
    zoomFrom = null;
  }

  function zoomBox() {
    if (zoom) return zoom;
    const box = make('div', 'pcg-live-zoom', document.body);
    box.hidden = true;
    const panel = make('div', 'pcg-live-zoom__panel', box);
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-modal', 'true');
    const close = make('button', 'pcg-live-zoom__close', panel);
    close.type = 'button';
    close.setAttribute('aria-label', 'Close');
    close.appendChild(icon(['M6 6l12 12', 'M18 6L6 18'], 18, 2.6));
    const slab = make('div', 'pcg-live-zoom__slab', panel);
    const bar = make('div', 'pcg-live-zoom__bar', panel);
    const caption = make('span', 'pcg-live-zoom__caption', bar);
    const link = make('a', 'pcg-live-zoom__pop', bar);
    link.target = '_blank';
    link.rel = 'noopener';
    link.appendChild(document.createTextNode('View in pop report '));
    make('span', '', link).textContent = '\u2197';
    link.lastChild.setAttribute('aria-hidden', 'true');

    close.addEventListener('click', closeZoom);
    /* box is the full-screen backdrop: a click on it, not on the panel, is a click outside. */
    box.addEventListener('click', (e) => {
      if (e.target === box) closeZoom();
    });
    document.addEventListener('keydown', (e) => {
      if (box.hidden) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closeZoom();
      } else if (e.key === 'Tab') {
        /* Keep focus in the panel: the close button and the pop report link. */
        const stops = [close, link];
        const at = stops.indexOf(document.activeElement);
        const next = e.shiftKey ? (at <= 0 ? stops.length - 1 : at - 1) : (at === stops.length - 1 ? 0 : at + 1);
        e.preventDefault();
        stops[next].focus();
      }
    });
    zoom = { box, panel, close, slab, caption, link };
    return zoom;
  }

  function openZoom(data, frameUrl, from) {
    const z = zoomBox();
    const full = httpUrl(data.imageUrl);
    const thumb = httpUrl(data.thumbUrl);
    const put = (url) => {
      const a = window.PCGSlab.render(Object.assign({}, data, { imageUrl: url }), { frameUrl });
      /* The pop report link under the slab is the keyboard stop; the slab itself is for the mouse. */
      a.tabIndex = -1;
      z.slab.textContent = '';
      z.slab.appendChild(a);
      return a;
    };
    const a = put(full || thumb);
    if (full && thumb && full !== thumb) {
      a.addEventListener('pcgslab:imageerror', () => {
        if (!z.box.hidden && z.slab.firstChild === a) put(thumb);
      });
    }
    z.panel.setAttribute('aria-label', 'Large view: ' + (a.getAttribute('aria-label') || 'slab'));
    z.caption.textContent = caption(data, ZONE, new Date());
    z.link.href = a.href;
    zoomFrom = from;
    z.box.hidden = false;
    document.documentElement.classList.add('pcg-live-zoom-open');
    z.close.focus();
  }

  function boot(scope) {
    (scope || document).querySelectorAll('.pcg-live-gallery[data-live-kind]').forEach(init);
  }

  /* For the test harness only: rotateMs may be lowered before the sections settle, and the
     formatters are unit-tested. */
  window.PCGLiveGallery = { version: VERSION, init, _test: { rotateMs: ROTATE_MS, gradedRange, caption, totalLine } };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  /* Theme editor: a re-rendered section brings a fresh root. */
  document.addEventListener('shopify:section:load', (e) => boot(e.target));
})();
